/* 
-----------------------------------------------------------------------------
| DATE SELECTION
-----------------------------------------------------------------------------
- description: its only purpose is to manage the value of booking.date
  and reset other values or navigate to other route if needed
*/
import { FC, useRef, useState } from "react";
import { MonthsAvailability } from "../../types";
import { _, parseDate, getMonthDifference } from "../../utils/helpers";
import { Icon } from "../Icon";
import { DateCell } from "./DateCell";
import { Calendar } from "./Calendar";
import { DEFAULT_MONTH_AVAILABILITY_DATE } from "../../utils/symbols";

interface DateSelectorProps {
    monthsAvailability: MonthsAvailability,
    displayedMonthsQty: number
    from: Date | number | string
    at?: Date | number | string
    value?: Date | number | string
    specialDates?: string[]
    showLegend?: boolean
    onMonthChange: (date: Date) => unknown
    onDateSelected: (date: string) => unknown
}

export const DateSelector:FC<DateSelectorProps> = ({
    monthsAvailability, 
    onDateSelected, 
    onMonthChange,
    displayedMonthsQty,
    from,
    value,
    at,
    showLegend,
    specialDates
}) => {

    const [currentMonthOffset, setCurrentMonthOffset] = useState<number>(getMonthDifference(from, at))

    const previousButton = useRef<HTMLButtonElement | null>(null)
    const nextButton = useRef<HTMLButtonElement | null>(null)
    const reference = new Date(from)
    const date = useRef<Date>(new Date(
        reference.getFullYear(), 
        reference.getMonth() + currentMonthOffset, 
        currentMonthOffset === 0 ? reference.getDate() : 1
    ))
    //recalculated on every render
    const prevMonthDate = new Date(date.current.getFullYear(), date.current.getMonth() - 1, 1)
    const nextMonthDate = new Date(date.current.getFullYear(), date.current.getMonth() + 1, 1)

    const goNextMonth = () => {
        if (displayedMonthsQty !== undefined && currentMonthOffset === displayedMonthsQty - 1) return
        date.current.setDate(1)
        date.current.setMonth(date.current.getMonth() + 1)
        updateCurrentMonthOffset(currentMonthOffset + 1)
        onMonthChange(date.current)
    }

    const goPreviousMonth = () => {
        if (currentMonthOffset === 0) return
        date.current.setMonth(date.current.getMonth() - 1)
        if (date.current.getMonth() === reference.getMonth()) date.current.setDate(reference.getDate())
        updateCurrentMonthOffset(currentMonthOffset - 1)
        onMonthChange(date.current)
    }

    const updateCurrentMonthOffset = (newState:number) => {
        let safeNewState = Math.max(0, newState)
        if (displayedMonthsQty !== undefined) safeNewState = Math.min(newState, displayedMonthsQty - 1)
        setCurrentMonthOffset(safeNewState)
    }
    
    return (
        <div className={`date-selector ${showLegend ? "show-legend" : ""}`} >
            <p className="text s32-700 mb-7 title" aria-hidden >{_('1da_selec__')}</p>
            <div className="calendar-container df">
                <Calendar 
                    id="date-picker"
                    allowPartialMonth
                    date={date.current}
                    value={value}
                    className="month df fd-c p-1 fs-0"
                    role="region"
                    aria-label={_('0ar_dates__')}
                    CellComponent={({dateInfo}) => {

                        const isHighlighted = specialDates?.some(date => {
                            const range = date.split('...')
                            if (range.length===2) return (
                                dateInfo.dateObject.getTime() >= new Date(range[0]).setHours(0, 0, 0, 0) && 
                                dateInfo.dateObject.getTime() <= new Date(range[1]).setHours(0, 0, 0, 0)
                            )
                            else return date === parseDate('yyyy-mm-dd', dateInfo.dateObject)
                        })
                        
                        const key = `${dateInfo.dateObject.getFullYear()}-${dateInfo.dateObject.getMonth() + 1}`
                        const details = monthsAvailability[key] ? 
                            monthsAvailability[key][dateInfo.dateString] : 
                            DEFAULT_MONTH_AVAILABILITY_DATE
                        
                        return ( 
                            <DateCell 
                                dateInfo={dateInfo} 
                                onDateSelected={onDateSelected} 
                                details={details} 
                                highlighted={isHighlighted}
                            />
                        )
                }}

                    ControlsComponent={() => (displayedMonthsQty) > 1 ? 
                    <div className="controls df" role="group" aria-label={`${parseDate('November yyyy', date.current)}. ${_('0ar_month__')}.`} >
                        <button 
                            ref={previousButton}
                            className={`button b-0 px-4 py-2 transparent r2 df jcc aic outline-shadow`} 
                            onClick={goPreviousMonth} 
                            disabled={currentMonthOffset === 0} 
                            aria-disabled={currentMonthOffset === 0}
                            aria-label={`${_('0ar_previ__')}, ${parseDate('November', prevMonthDate)}`}
                        >
                            <Icon icon="arrow" aria-hidden />
                        </button>
                        <button 
                            ref={nextButton}
                            className="button b-0 px-4 py-2 transparent r2 df jcc aic outline-shadow" 
                            onClick={goNextMonth} 
                            disabled={!!(displayedMonthsQty && currentMonthOffset === displayedMonthsQty - 1)} 
                            aria-disabled={!!(displayedMonthsQty && currentMonthOffset === displayedMonthsQty - 1)}
                            aria-label={`.${_('0ar_nextm__')}, ${parseDate('November', nextMonthDate)}`}
                        >
                            <Icon icon="arrow" className="rt-180" aria-hidden />
                        </button>
                    </div> : null}
                />

                <div className="calendar-legend df fd-c" role="region" aria-label={_('0ar_helpc__')}>
                    {/* <HelpButton className="mb-8"/> */}

                    <div className="df fd-c icons-description mb-7 jcsb" aria-hidden>
                        <p className="text softer s16-400 df mb-3 nw">
                            <Icon icon="fire" className="jcfs"/><span>{_('0ge_onlya__')}</span>
                        </p>
                        <p className="text softer s16-400 df nw mb-3">
                            <Icon icon="star" className="jcfs"/><span>{_('2sh_speci__')}</span>
                        </p>
                        <p className="text softer s16-400 df nw ">
                            <Icon icon="circle" className="jcfs"/><span>{_('1da_soldo__')}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}